import React, { createContext, useContext, useState, useEffect } from "react";
import querySeanQL from "../requests/SeanQL";
import { ECLLoader } from "../../eclComponents/";
import { ECLUnsafeStringifyULID, ECLUnsafeStringifyUUID } from "../ECLUUID";
import { ToastContext } from "./ToastContext";
import { AuthContext } from "./AuthContext";

/**
 * Ontology context contains some basic data from the server's ontology.
 * @type {Object}
 * @property {array} objects - returns a list of all objects and their properties
 * */
export const OntologyContext = createContext();

export function OntologyContextProvider(props) {
   const [error, setError] = useState(null);
   const [ontology, setOntology] = useState(null);
   const addToast = useContext(ToastContext);
   const auth = useContext(AuthContext);

   async function getOntology() {
      //Get all the object ontology data
      const [
         objectTypes,
         objectProperties,
         dataTypes,
         numericProperties,
         textProperties,
         backlinkData,
      ] = await Promise.all([
         querySeanQL("object_types"),
         querySeanQL("object_type_properties"),
         querySeanQL("data_types"),
         querySeanQL("data_type_properties_numeric"),
         querySeanQL("data_type_properties_text"),
         querySeanQL("object_type_backlinks"),
      ]);

      //This pattern courtesy of SO-1215.  Expected key names are recorded, then we find the index in the column to
      //access the correct index in the row.
      let objectTypesKeys = {
         id: null,
         name: null,
         description: null,
         "display property": null,
      };
      Object.keys(objectTypesKeys).forEach((o) => {
         objectTypesKeys[o] = objectTypes.columns.findIndex(
            (c) => c.name === o,
         );
      });
      if (Object.values(objectTypesKeys).some((k) => k === null)) {
         throw new Error("Missing expected keys on object_types query");
      }

      let objectPropertiesKeys = {
         name: null,
         id: null,
         object_id: null,
         type_id: null,
         linked_object_type: null,
         link_owns_data: null,
         description: null,
      };
      Object.keys(objectPropertiesKeys).forEach((o) => {
         objectPropertiesKeys[o] = objectProperties.columns.findIndex(
            (c) => c.name === o,
         );
      });
      if (Object.values(objectPropertiesKeys).some((k) => k === null)) {
         throw new Error(
            "Missing expected keys on object_type_properties query",
         );
      }

      let dataTypesKeys = {
         id: null,
         name: null,
         type: null,
         description: null,
      };
      Object.keys(dataTypesKeys).forEach((o) => {
         dataTypesKeys[o] = dataTypes.columns.findIndex((c) => c.name === o);
      });
      if (Object.values(dataTypesKeys).some((k) => k === null)) {
         throw new Error("Missing expected keys on data_types query");
      }

      let numericPropertiesKeys = {
         id: null,
         "property name": null,
         value: null,
      };
      Object.keys(numericPropertiesKeys).forEach((o) => {
         numericPropertiesKeys[o] = numericProperties.columns.findIndex(
            (c) => c.name === o,
         );
      });
      if (Object.values(numericPropertiesKeys).some((k) => k === null)) {
         throw new Error("Missing expected keys on data_types query");
      }

      let textPropertiesKeys = {
         id: null,
         "property name": null,
         value: null,
      };
      Object.keys(textPropertiesKeys).forEach((o) => {
         textPropertiesKeys[o] = textProperties.columns.findIndex(
            (c) => c.name === o,
         );
      });
      if (Object.values(textPropertiesKeys).some((k) => k === null)) {
         throw new Error(
            "Missing expected keys on data_type_properties_text query",
         );
      }

      let backlinkDataKeys = {
         id: null,
         from_object_id: null,
         property_type_id: null,
         backlink_name: null,
      };
      Object.keys(backlinkDataKeys).forEach((o) => {
         backlinkDataKeys[o] = backlinkData.columns.findIndex(
            (c) => c.name === o,
         );
      });
      if (Object.values(backlinkDataKeys).some((k) => k === null)) {
         throw new Error("Missing expected keys on data_types query");
      }

      const objects = objectTypes.rows.map((o) => {
         return {
            UUID: ECLUnsafeStringifyUUID(o[objectTypesKeys["id"]]),
            ULID: ECLUnsafeStringifyULID(o[objectTypesKeys["id"]]),
            name: o[objectTypesKeys["name"]],
            descriptions: o[objectTypesKeys["description"]],
            displayProperty:
               ECLUnsafeStringifyULID(
                  o[objectTypesKeys["display property"]],
               ) !== "00000000000000000000000000" &&
               objectProperties.rows.find(
                  (z) =>
                     ECLUnsafeStringifyULID(z[objectPropertiesKeys["id"]]) ==
                     ECLUnsafeStringifyULID(
                        o[objectTypesKeys["display property"]],
                     ),
               ),
            properties: objectProperties.rows
               .filter(
                  (p) =>
                     ECLUnsafeStringifyULID(
                        p[objectPropertiesKeys["object_id"]],
                     ) === ECLUnsafeStringifyULID(o[objectTypesKeys["id"]]),
               )
               .filter((p) => {
                  let backlinks = backlinkData.rows.filter(
                     (r) =>
                        ECLUnsafeStringifyULID(
                           r[backlinkDataKeys["from_object_id"]],
                        ) === ECLUnsafeStringifyULID(o[objectTypesKeys["id"]]),
                  );
                  let backlink = backlinks.some(
                     (b) =>
                        ECLUnsafeStringifyULID(b[backlinkDataKeys["id"]]) ===
                           ECLUnsafeStringifyULID(
                              p[objectPropertiesKeys["type_id"]],
                           ) &&
                        ECLUnsafeStringifyULID(
                           p[objectPropertiesKeys["id"]],
                        ) ===
                           ECLUnsafeStringifyULID(
                              b[backlinkDataKeys["property_type_id"]],
                           ),
                  );
                  return !backlink;
               })
               .map((p) => {
                  const dataType = dataTypes.rows.find(
                     (t) =>
                        ECLUnsafeStringifyULID(t[dataTypesKeys["id"]]) ===
                        ECLUnsafeStringifyULID(
                           p[objectPropertiesKeys["type_id"]],
                        ),
                  );
                  const numericProps = numericProperties.rows.filter(
                     (n) =>
                        ECLUnsafeStringifyULID(
                           n[numericPropertiesKeys["id"]],
                        ) ===
                        ECLUnsafeStringifyULID(
                           p[objectPropertiesKeys["type_id"]],
                        ),
                  );
                  const textProps = textProperties.rows.filter(
                     (n) =>
                        ECLUnsafeStringifyULID(
                           n[numericPropertiesKeys["id"]],
                        ) ===
                        ECLUnsafeStringifyULID(
                           p[objectPropertiesKeys["type_id"]],
                        ),
                  );
                  //Basic Type
                  let theType = {
                     name: dataType[dataTypesKeys["name"]],
                     primitive: dataType[dataTypesKeys["type"]],
                     description: dataType[dataTypesKeys["description"]],
                     backlink: false,
                  };
                  //Add properties in
                  numericProps.forEach((property) => {
                     theType[
                        property[1].toLocaleLowerCase().replaceAll(" ", "_")
                     ] = property[2];
                  });

                  textProps.forEach((property) => {
                     theType[
                        property[1].toLocaleLowerCase().replaceAll(" ", "_")
                     ] = property[2];
                  });
                  // //backlink data for each property
                  // let backlinks = backlinkData.rows.filter(r => ECLUnsafeStringifyULID(r[1]) === ECLUnsafeStringifyULID(o[0])) //Find object from
                  // //Finbd
                  // theType.backlink = backlinks.some(b => (ECLUnsafeStringifyULID(b[0]) === ECLUnsafeStringifyULID(p[4]) && (ECLUnsafeStringifyULID(p[1]) === ECLUnsafeStringifyULID(b[2]))))

                  return {
                     name: p[objectPropertiesKeys["name"]],
                     type: theType,
                     link:
                        ECLUnsafeStringifyULID(
                           p[objectPropertiesKeys["linked_object_type"]],
                        ) !== "00000000000000000000000000" &&
                        ECLUnsafeStringifyULID(
                           p[objectPropertiesKeys["linked_object_type"]],
                        ),
                     ownsData: p[objectPropertiesKeys["link_owns_data"]],
                     UUID: ECLUnsafeStringifyUUID(
                        p[objectPropertiesKeys["id"]],
                     ),
                     ULID: ECLUnsafeStringifyULID(
                        p[objectPropertiesKeys["id"]],
                     ),
                  };
               }),
         };
      });
      objects.forEach((o) => {
         const backlinks = backlinkData.rows.filter(
            (r) => ECLUnsafeStringifyULID(r[backlinkDataKeys["id"]]) === o.ULID,
         );
         let backlinkProperties = [];
         backlinks.forEach((b) =>
            backlinkProperties.push(
               objectProperties.rows
                  .filter(
                     (p) =>
                        ECLUnsafeStringifyULID(
                           p[objectPropertiesKeys["id"]],
                        ) ===
                        ECLUnsafeStringifyULID(
                           b[backlinkDataKeys["property_type_id"]],
                        ),
                  )
                  .map((p) => {
                     const dataType = dataTypes.rows.find(
                        (t) =>
                           ECLUnsafeStringifyULID(t[dataTypesKeys["id"]]) ===
                           ECLUnsafeStringifyULID(
                              p[objectPropertiesKeys["type_id"]],
                           ),
                     );
                     const numericProps = numericProperties.rows.filter(
                        (n) =>
                           ECLUnsafeStringifyULID(
                              n[numericPropertiesKeys["id"]],
                           ) ===
                           ECLUnsafeStringifyULID(
                              p[objectPropertiesKeys["type_id"]],
                           ),
                     );
                     const textProps = textProperties.rows.filter(
                        (n) =>
                           ECLUnsafeStringifyULID(
                              n[numericPropertiesKeys["id"]],
                           ) ===
                           ECLUnsafeStringifyULID(
                              p[objectPropertiesKeys["type_id"]],
                           ),
                     );
                     //Basic Type
                     let theType = {
                        name: dataType[1],
                        primitive: dataType[2],
                        description: dataType[3],
                        backlink: true,
                     };
                     //Add properties in
                     numericProps.forEach((property) => {
                        theType[
                           property[1].toLocaleLowerCase().replaceAll(" ", "_")
                        ] = property[2];
                     });

                     textProps.forEach((property) => {
                        theType[
                           property[1].toLocaleLowerCase().replaceAll(" ", "_")
                        ] = property[2];
                     });
                     return {
                        name: b[backlinkDataKeys["backlink_name"]],
                        type: theType,
                        link: ECLUnsafeStringifyULID(
                           b[backlinkDataKeys["from_object_id"]],
                        ),
                        ownsData: p[objectPropertiesKeys["link_owns_data"]],
                        UUID: ECLUnsafeStringifyUUID(
                           p[objectPropertiesKeys["id"]],
                        ),
                        ULID: ECLUnsafeStringifyULID(
                           p[objectPropertiesKeys["id"]],
                        ),
                     };
                  }),
            ),
         );
         backlinkProperties.forEach((b) =>
            o.properties.push(b[backlinkDataKeys["id"]]),
         );
      });
      //Leave space for other stuff (Data assets?)
      setOntology({
         objects: objects,
      });
   }

   useEffect(() => {
      getOntology().catch((error) => {
         setError("Error fetching ontology");
         addToast("Error fetching ontology");
         console.error(error);
         auth.logout();
      });
   }, []);

   if (error) {
      return <ECLLoader message={error} />;
   }

   return (
      <OntologyContext.Provider value={ontology}>
         {props.children}
      </OntologyContext.Provider>
   );
}
